import React, { useRef } from 'react';
import { Button, ButtonProps, IconButton, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { CSVLink } from 'react-csv';
import { cleanCsvData } from '../../../util/CsvDataUtil';

type CsvData = Parameters<typeof cleanCsvData>[0];

type CSVLinkInstance = { link: HTMLAnchorElement };

type TypedCSVLinkProps = {
  ref?: React.Ref<CSVLinkInstance>;
  data: object[];
  filename: string;
  separator: string;
  style?: React.CSSProperties;
  [key: string]: unknown;
};

const TypedCSVLink = CSVLink as unknown as React.ComponentType<TypedCSVLinkProps>;

export interface ExportCSVButtonProps extends Omit<ButtonProps, 'data'> {
  /** Formatted rows to export. Kept for compatibility with the previous JS component. */
  data?: unknown[];
  /** Formatted rows to export. Preferred prop for new callers. */
  reportData?: unknown[];
  /** Name of the downloaded CSV file. */
  reportName?: string;
  /** Button label. */
  title?: string;
  /** Props passed through to react-csv's CSVLink, such as headers. */
  csvLinkProps?: {
    headers?: Array<{ label: string; key: string }> | string[];
    separator?: string;
    enclosingCharacter?: string;
    [key: string]: unknown;
  };
  /** Class applied to the link wrapper. */
  linkClassName?: string;
  /** Render as an icon-only button for compact toolbars. */
  iconOnly?: boolean;
  /** Icon used when iconOnly is true. */
  icon?: React.ReactNode;
  /** Tooltip used when iconOnly is true. */
  tooltipTitle?: string;
  /** Accessible label used when iconOnly is true. */
  iconAriaLabel?: string;
}

const ExportCSVButton = ({
  data,
  reportData,
  reportName = 'Report',
  title = 'DOWNLOAD REPORT',
  csvLinkProps,
  linkClassName = 'csvLink-container',
  disabled = false,
  className = 'ss-button-primary ss-excel-icon',
  variant = 'contained',
  iconOnly = false,
  icon,
  tooltipTitle,
  iconAriaLabel,
  ...buttonProps
}: ExportCSVButtonProps) => {
  const rows = reportData || data || [];
  const { separator = ',', ...restCsvLinkProps } = csvLinkProps || {};
  const label = iconAriaLabel || tooltipTitle || title;

  const csvLinkRef = useRef<CSVLinkInstance>(null);

  const handleButtonClick = () => {
    if (disabled) return;
    csvLinkRef.current?.link.click();
  };

  const button = iconOnly ? (
    <Tooltip title={tooltipTitle || title}>
      <span>
        <IconButton
          aria-label={label}
          className={className}
          disabled={disabled}
          onClick={handleButtonClick}
          {...buttonProps}
        >
          {icon || <FileDownloadOutlinedIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Button
      type="button"
      variant={variant}
      className={className}
      disabled={disabled}
      onClick={handleButtonClick}
      {...buttonProps}
    >
      {title}
    </Button>
  );

  return (
    <div className={linkClassName}>
      {/*
        Hidden CSVLink triggered programmatically via ref.
        This avoids nesting <button> inside <a>, which is invalid HTML
        and breaks disabled state / keyboard / screen-reader behaviour.
        restCsvLinkProps is spread first so callers cannot override
        filename/data and bypass cleanCsvData (CSV-injection mitigation).
      */}
      <TypedCSVLink
        ref={csvLinkRef}
        {...restCsvLinkProps}
        filename={reportName}
        data={cleanCsvData(rows as CsvData) as object[]}
        separator={separator}
        style={{ display: 'none' }}
      />
      {button}
    </div>
  );
};

export default ExportCSVButton;
